import { StatusPill } from '@vnetwork/ui/data';
import type { ColumnDef } from '@vnetwork/ui/data';
import type { ProbeResult, WatchpointType } from '@/api/healthcheck';
import { fmtLatency, fmtDateTime } from '../shared';

/**
 * Type-aware probe history + trend chart. Each monitor type records a different
 * signal in `ProbeResult.metadata` (tls → cert expiry, dns → resolved records,
 * elastic/waap/delivery → matched groups, protocol → status/latency), so the
 * history columns and the trend metric adapt to the type instead of always
 * showing latency.
 */
function md(r: ProbeResult): Record<string, unknown> {
  const m = r.metadata;
  return m && typeof m === 'object' ? (m as Record<string, unknown>) : {};
}
function num(v: unknown): number | null {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}
function str(v: unknown): string {
  return v == null ? '' : String(v);
}

const muted = (v: React.ReactNode) => (
  <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>{v}</span>
);
const errCell = (r: ProbeResult) => (
  <span style={{ color: 'var(--vn-danger-text)', fontSize: 'var(--vn-font-size-sm)' }}>{r.error_message || ''}</span>
);
const timeCol: ColumnDef<ProbeResult, unknown> = {
  id: 'time', header: 'Time', enableSorting: false, cell: ({ row }) => muted(fmtDateTime(row.original.timestamp)),
};
const statusCol: ColumnDef<ProbeResult, unknown> = {
  id: 'status', header: 'Status', enableSorting: false,
  cell: ({ row }) => (
    <StatusPill tone={row.original.is_up ? 'success' : 'danger'}>{row.original.is_up ? 'OK' : 'Fail'}</StatusPill>
  ),
};

/** Columns for the Check-history table, adapted to the monitor type. */
export function historyColumns(type: WatchpointType): ColumnDef<ProbeResult, unknown>[] {
  if (type === 'tls') {
    return [
      timeCol, statusCol,
      { id: 'days', header: 'Days left', enableSorting: false, cell: ({ row }) => { const d = num(md(row.original).days_until_expiry); return d != null ? `${d} days` : '—'; } },
      { id: 'expires', header: 'Expires', enableSorting: false, cell: ({ row }) => muted(str(md(row.original).not_after) || '—') },
      { id: 'issuer', header: 'Issuer', enableSorting: false, cell: ({ row }) => muted(str(md(row.original).issuer) || '—') },
      { id: 'error', header: 'Error', enableSorting: false, cell: ({ row }) => errCell(row.original) },
    ];
  }
  if (type === 'dns') {
    return [
      timeCol, statusCol,
      { id: 'records', header: 'Resolved records', enableSorting: false, cell: ({ row }) => { const recs = md(row.original).records; return Array.isArray(recs) ? (recs as unknown[]).map(str).join(', ') || '—' : '—'; } },
      { id: 'rtype', header: 'Type', enableSorting: false, cell: ({ row }) => muted(str(md(row.original).record_type) || '—') },
      { id: 'error', header: 'Error', enableSorting: false, cell: ({ row }) => errCell(row.original) },
    ];
  }
  if (type === 'elastic' || type === 'waap' || type === 'delivery') {
    return [
      timeCol, statusCol,
      { id: 'groups', header: 'Groups', enableSorting: false, cell: ({ row }) => str(num(md(row.original).total_items) ?? '—') },
      { id: 'top', header: 'Top group', enableSorting: false, cell: ({ row }) => muted(topGroup(md(row.original))) },
      { id: 'error', header: 'Error', enableSorting: false, cell: ({ row }) => errCell(row.original) },
    ];
  }
  // http | https | tcp
  return [
    timeCol, statusCol,
    { id: 'code', header: 'Code', enableSorting: false, cell: ({ row }) => row.original.status_code ?? '—' },
    { id: 'latency', header: 'Latency', enableSorting: false, cell: ({ row }) => fmtLatency(row.original.latency_ms) },
    { id: 'region', header: 'Region', enableSorting: false, cell: ({ row }) => muted(row.original.region || '—') },
    { id: 'error', header: 'Error', enableSorting: false, cell: ({ row }) => errCell(row.original) },
  ];
}

function topGroup(m: Record<string, unknown>): string {
  const items = Array.isArray(m.data_items) ? (m.data_items as Record<string, unknown>[]) : [];
  if (!items.length) return '—';
  const it = items[0];
  const dim = Object.entries(it).find(([k]) => k !== 'value' && k !== 'count');
  const metric = it.value ?? it.count;
  return dim ? `${str(dim[1])} (${str(metric)})` : str(metric);
}

export interface TrendChart {
  data: Record<string, unknown>[];
  yKey: string;
  label: string;
  kind: 'area' | 'line';
  format: (v: string | number | undefined) => React.ReactNode;
}

export interface StatusCodeChart {
  /** One row per check: the timestamp plus a `1` under the check's status class. */
  data: Record<string, unknown>[];
  /** Only the classes that actually occurred, so the legend stays clean. */
  series: { key: string; label: string; color: string }[];
}

/** HTTP status classes, ordered, with their palette color + membership test. */
const HTTP_CLASSES = [
  { key: '2xx', label: '2xx Success', color: 'var(--vn-color-green-400)', has: (c: number) => c >= 200 && c < 300 },
  { key: '3xx', label: '3xx Redirect', color: 'var(--vn-color-blue-500)', has: (c: number) => c >= 300 && c < 400 },
  { key: '4xx', label: '4xx Client error', color: 'var(--vn-color-amber-400)', has: (c: number) => c >= 400 && c < 500 },
  { key: '5xx', label: '5xx Server error', color: 'var(--vn-color-red-400)', has: (c: number) => c >= 500 && c < 600 },
] as const;
/** Checks that returned no HTTP code (connection failure / timeout). */
const NO_RESPONSE = { key: 'err', label: 'No response', color: 'var(--vn-color-neutral-400)' };

/** Status class key for a probe, or `err` when it has no usable HTTP code. */
function statusClass(r: ProbeResult): string {
  const code = num(r.status_code);
  if (code == null) return NO_RESPONSE.key;
  return HTTP_CLASSES.find((c) => c.has(code))?.key ?? NO_RESPONSE.key;
}

/**
 * Status-code timeline for HTTP-family monitors: each check becomes a unit bar
 * colored by its HTTP class, so a wall of green with red bars where 5xx occurred
 * reads the response-code history at a glance. Returns null for non-HTTP types
 * or when no check carries a status code.
 */
export function buildStatusCodeChart(type: WatchpointType, metrics: ProbeResult[]): StatusCodeChart | null {
  if (type !== 'http' && type !== 'https' && type !== 'tcp') return null;
  // Chronological (probe results arrive newest-first).
  const ordered = [...metrics].reverse();
  const present = new Set<string>();
  const data = ordered.map((r) => {
    const cls = statusClass(r);
    present.add(cls);
    return { t: clock(r.timestamp), [cls]: 1 } as Record<string, unknown>;
  });
  // A chart is only meaningful once at least one check reported a real HTTP code.
  const hasCode = HTTP_CLASSES.some((c) => present.has(c.key));
  if (!hasCode) return null;

  const series = [...HTTP_CLASSES, NO_RESPONSE]
    .filter((c) => present.has(c.key))
    .map((c) => ({ key: c.key, label: c.label, color: c.color }));
  return { data, series };
}

function clock(iso: string): string {
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
}

/** The trend series to plot for a type, or null when there's no useful trend. */
export function buildTrendChart(type: WatchpointType, metrics: ProbeResult[]): TrendChart | null {
  // Chronological (probe results arrive newest-first).
  const ordered = [...metrics].reverse();
  const pick = (r: ProbeResult): number | null => {
    if (type === 'tls') return num(md(r).days_until_expiry);
    if (type === 'dns') { const recs = md(r).records; return Array.isArray(recs) ? (recs as unknown[]).length : null; }
    if (type === 'elastic' || type === 'waap' || type === 'delivery') return num(md(r).total_items);
    return num(r.latency_ms);
  };
  const data = ordered.map((r) => ({ t: clock(r.timestamp), y: pick(r) })).filter((d) => d.y != null);
  if (!data.length) return null;

  if (type === 'tls') return { data, yKey: 'y', label: 'Days until expiry', kind: 'line', format: (v) => `${v} days` };
  if (type === 'dns') return { data, yKey: 'y', label: 'Resolved records', kind: 'area', format: (v) => String(v) };
  if (type === 'elastic' || type === 'waap' || type === 'delivery')
    return { data, yKey: 'y', label: 'Groups matched', kind: 'area', format: (v) => String(v) };
  return { data, yKey: 'y', label: 'Latency', kind: 'area', format: (v) => fmtLatency(v as number) };
}
